/**
 * WholesaleSlabsEditor — reusable inline editor for a list of
 * `{min_qty, discount_pct}` rows. Slabs are kept sorted ASC by qty on
 * every mutation so the parent never has to re-sort before persisting.
 *
 * Props:
 *   slabs         — current array (can be undefined)
 *   onChange(arr) — fires with the canonicalised array (sorted, deduped)
 *   compact?      — pass true to render a tighter version inside small
 *                   edit modals (default false).
 */
import React from "react";
import { Plus, Trash2, Tag } from "lucide-react";

const blank = () => ({ min_qty: "", discount_pct: "" });

function canonicalise(rows) {
  const out = [];
  const seen = new Set();
  for (const r of rows) {
    const mq = parseInt(r.min_qty, 10);
    const dp = parseFloat(r.discount_pct);
    if (!Number.isFinite(mq) || !Number.isFinite(dp)) continue;
    if (mq < 2 || dp <= 0 || dp > 90) continue;
    if (seen.has(mq)) continue;
    seen.add(mq);
    out.push({ min_qty: mq, discount_pct: dp });
  }
  out.sort((a, b) => a.min_qty - b.min_qty);
  return out;
}

export default function WholesaleSlabsEditor({ slabs = [], onChange, compact = false }) {
  const rows = slabs.length > 0 ? slabs.map(s => ({ ...s })) : [blank()];

  const setRow = (i, k, v) => {
    const next = rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r);
    onChange(canonicalise(next));
  };

  const addRow = () => onChange([...rows, blank()]);
  const removeRow = (i) => onChange(canonicalise(rows.filter((_, idx) => idx !== i)));

  return (
    <div className={compact ? "" : "card p-4 mt-3"} data-testid="wholesale-slabs-editor">
      <div className="flex items-center gap-2 mb-2">
        <Tag size={14} className="text-[#D4FF00]"/>
        <div className="text-[10px] uppercase tracking-widest font-bold">Wholesale / bulk discount</div>
        <span className="text-[10px] text-[#A1A1AA]">— buyers buying in bulk auto-receive these slab discounts.</span>
      </div>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={`slab-${i}-${r.min_qty}`} className="flex items-center gap-2"
            data-testid={`slab-row-${i}`}>
            <div className="flex-1 grid grid-cols-2 gap-2">
              <label className="block">
                <span className="block text-[9px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-0.5">Min qty</span>
                <input type="number" min="2" max="100000" step="1"
                  className="input-dark text-xs w-full" placeholder="e.g. 10"
                  value={r.min_qty} onChange={e => setRow(i, "min_qty", e.target.value)}
                  data-testid={`slab-qty-${i}`}/>
              </label>
              <label className="block">
                <span className="block text-[9px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-0.5">Discount %</span>
                <input type="number" min="0.5" max="90" step="0.5"
                  className="input-dark text-xs w-full" placeholder="e.g. 10"
                  value={r.discount_pct} onChange={e => setRow(i, "discount_pct", e.target.value)}
                  data-testid={`slab-pct-${i}`}/>
              </label>
            </div>
            <button type="button" onClick={() => removeRow(i)}
              data-testid={`slab-remove-${i}`}
              className="w-8 h-8 rounded-lg bg-[#3D2845] hover:bg-[#FF007F] hover:text-white flex items-center justify-center shrink-0">
              <Trash2 size={12}/>
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={addRow} data-testid="slab-add-row"
        className="mt-2 btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1">
        <Plus size={11}/> Add slab
      </button>
      {rows.filter(r => r.min_qty && r.discount_pct).length >= 2 && (
        <div className="mt-3 text-[10px] text-[#A1A1AA]">
          ✓ Buyer will auto-receive the highest matching discount based on their cart quantity.
        </div>
      )}
    </div>
  );
}
